const _keyBy = require('lodash/keyBy');
const _isEmpty = require('lodash/isEmpty');
const _get = require('lodash/get');
const _orderBy = require('lodash/orderBy');
const _uniqBy = require('lodash/uniqBy');
const _groupBy = require('lodash/groupBy');
const _cloneDeep = require('lodash/cloneDeep');
const _findIndex = require('lodash/findIndex');
const _flatMap = require('lodash/flatMap');
const _sortBy = require('lodash/sortBy');

const Delimiters = {
    COMMA: ',',
    BAR: '|',
    DOT: '.'
}

const UKProductTypes = {
    PIZZA: 'pizza',
    SIDE: 'side',
    DESSERT: 'dessert',
    DIP: 'dip',
    DRINK: 'drink',
    TOPPING: 'topping',
    SAUCE: 'sauce',
    CHEESE: 'cheese',
    CRUST: 'crust',
    DEAL: 'deal'
}

const SINGLE_PRODUCT_TYPES = [
    UKProductTypes.DIP,
    UKProductTypes.DESSERT,
    UKProductTypes.DRINK,
    UKProductTypes.SIDE,
    UKProductTypes.DEAL
];

function innerJoin(
    source,
    contents,
    identities,
    fMap
) {
    if (_isEmpty(source) || _isEmpty(contents)) {
        return [];
    }
    const [sourceIdentifier, contentIdentifier] = identities;
    const sourceIds = sourceIdentifier.split(Delimiters.COMMA);
    const contentIds = contentIdentifier.split(Delimiters.COMMA);
    //Convert array to Map
    const contentMap = _keyBy(contents, (item) => {
        return contentIds.map((id) => _get(item, id, '')).join(Delimiters.BAR);
    });
    //Map item
    const mapItem = (sourceItem) => {
        const sourceKeyValue = sourceIds.map((id) => _get(sourceItem, id, '')).join(Delimiters.BAR);
        const content = contentMap[sourceKeyValue];
        if (!content) {
            return;
        }
        return fMap(sourceItem, content);
    };
    return source.map(mapItem).filter((item) => item);
}

export default class ExportOOS {
    PRICE_ITEMS = [];
    ALL_OOS_ITEMS = [];
    ALL_STORES = [];
    ALL_INVENTORY_ITEMS = [];
    unavailableProductSkus = [];

    constructor(ALL_STORES, PRICE_ITEMS, ALL_INVENTORY_ITEMS, unavailableProductSkus) {
        this.PRICE_ITEMS = PRICE_ITEMS;
        this.ALL_STORES = ALL_STORES;
        this.ALL_INVENTORY_ITEMS = ALL_INVENTORY_ITEMS;
        this.unavailableProductSkus = unavailableProductSkus;
        if (_isEmpty(this.ALL_OOS_ITEMS)) {
            this.ALL_OOS_ITEMS = this.PRICE_ITEMS.filter(
                (p) => p.available === false && !_isEmpty(p.store) && !_isEmpty(p.store.code)
            );
        }
    }

    getStoreByCode(storeCode) {
        return this.ALL_STORES.find((s) => s.code === storeCode);
    }

    getAllInventoryItems() {
        return this.ALL_INVENTORY_ITEMS;
    }

    getOutOfStockTitles(store) {
        const storeCode = `${store.code}`;
        const storeId = `${store.id}`;

        if (_isEmpty(storeId)) {
            return [];
        }

        const singleItemTypes = [
            UKProductTypes.SIDE,
            UKProductTypes.DESSERT,
            UKProductTypes.DIP,
            UKProductTypes.DRINK,
            UKProductTypes.DEAL
        ];
        const pizzaItemType = [UKProductTypes.PIZZA];
        const pizzaByCrustItemType = [UKProductTypes.CRUST];
        const productTypes = [...singleItemTypes, ...pizzaItemType, ...pizzaByCrustItemType].join(',');
        const entries = this.getOutOfStockEntries(+storeId, productTypes);

        if (_isEmpty(entries)) {
            return [];
        }

        const [singleEntries, pizzaEntries, crustEntries] = [
            singleItemTypes,
            pizzaItemType,
            pizzaByCrustItemType
        ].map((type) => entries.filter((entry) => type.includes(entry.inventoryItem.type)));

        const groupKey = (entry) => {
            return singleItemTypes.includes(entry.inventoryItem.type)
                ? [`${entry.inventoryItem.title}|${entry.inventoryItem.skus[0].code}|${entry.inventoryItem.type}`]
                : [`${entry.inventoryItem.title}||${entry.inventoryItem.type}`];
        };
        const items = [singleEntries, pizzaEntries, crustEntries].map((entries) => {
            if (_isEmpty(entries)) {
                return [];
            }
            const titles = Object.keys(_groupBy(entries, groupKey)).map((title) => {
                const arr = title.split(Delimiters.BAR);
                return {
                    type: arr[2].replace(/^./, (str) => str.toUpperCase()),
                    name: arr[0],
                    cluster: _get(store, 'storeGroup.name', ''),
                    hutid: storeCode
                };

            });

            return _sortBy(titles, function (t) {
                return t.title;
            });
        });

        return _flatMap(items);
    }

    getOutOfStockEntries(storeId, productTypes) {
        try {
            // Resolve tenant
            const store = this.ALL_STORES.find((s) => s.id === storeId);
            // Get product content from ms-content
            const inventoryItems = this.searchInventoryItems(productTypes);

            if (_isEmpty(inventoryItems)) {
                return [];
            }

            const oosPrices = this.ALL_OOS_ITEMS.filter((p) => p.storeId === store.id);

            if (_isEmpty(oosPrices)) {
                return [];
            }

            let pizzaInventoryItems = [];
            let crustInventoryItems = [];
            let oosPizzaInventoryItemIds = [];
            let oosCrustInventoryItemIds = [];
            let groupedOosPizzaItems = {};
            let groupedOosCrustItems = {};
            const oosSingleItems = [];
            const oosPizzaCrustItems = [];
            if (
                productTypes.includes(UKProductTypes.DESSERT) ||
                productTypes.includes(UKProductTypes.DIP) ||
                productTypes.includes(UKProductTypes.DRINK) ||
                productTypes.includes(UKProductTypes.SIDE) ||
                productTypes.includes(UKProductTypes.DEAL)
            ) {
                const singleInventoryItems = inventoryItems.filter((item) => {
                    return SINGLE_PRODUCT_TYPES.includes(item.type) && !(UKProductTypes.DEAL === item.type
                        && this.unavailableProductSkus.includes((_get(item, 'skus', [])[0] || { id: 0, code: '0' }).code))
                });
                oosSingleItems.push(
                    ...innerJoin(
                        oosPrices,
                        singleInventoryItems,
                        ['inventoryItemId', 'id'],
                        (priceItem, inventoryItem) => {
                            return {
                                ...priceItem,
                                inventoryItem
                            };
                        }
                    )
                );
            }

            // getInventoryItemIdsByTitle func gets inventoryItemIds from groupedInventoryItems
            const getInventoryItemIdsByTitle = (inventoryItems, groupedTitles) => {
                const inventoryItemIds = [];
                for (const groupedTitle of groupedTitles) {
                    inventoryItemIds.push(
                        ...inventoryItems.filter((inventoryItem) => inventoryItem.title === groupedTitle).map((item) => item.id)
                    );
                }
                return inventoryItemIds;
            };

            // getInventoryItemIdsByTitle func gets inventoryItemIds from groupedInventoryItems
            const innerJoinPricesWithInventoryItems = (
                prices,
                inventoryItems
            ) => {
                return innerJoin(prices, inventoryItems, ['inventoryItemId', 'id'], (priceItem, inventoryItem) => {
                    return {
                        ...priceItem,
                        inventoryItem
                    };
                });
            };

            // getOosItems gets all valid oos items
            const getOosItems = (groupedOosItems, groupedInStockItems) => {
                const oosItems = [];
                for (const pizzaName of Object.keys(groupedOosItems)) {
                    if (!groupedInStockItems[pizzaName] || groupedInStockItems[pizzaName].length === 0) {
                        oosItems.push(...groupedOosItems[pizzaName]);
                        continue;
                    }
                    if (groupedInStockItems[pizzaName].length > groupedOosItems[pizzaName].length) {
                        continue;
                    }
                    const isOosGroup = groupedInStockItems[pizzaName].every(
                        (item) => _findIndex(groupedOosItems[pizzaName], { inventoryItemId: item.inventoryItemId }) > -1
                    );
                    if (isOosGroup) {
                        oosItems.push(...groupedOosItems[pizzaName]);
                    }
                }
                return oosItems;
            };

            if (productTypes.includes(UKProductTypes.PIZZA)) {
                pizzaInventoryItems = inventoryItems.filter((item) => item.type === UKProductTypes.PIZZA);
                const oosPizzaItems = innerJoinPricesWithInventoryItems(
                    _uniqBy(oosPrices, (price) => price.inventoryItemId),
                    pizzaInventoryItems
                );
                groupedOosPizzaItems = _groupBy(oosPizzaItems, (item) => [item.inventoryItem.title]);
                oosPizzaInventoryItemIds = getInventoryItemIdsByTitle(pizzaInventoryItems, Object.keys(groupedOosPizzaItems));
            }
            if (productTypes.includes(UKProductTypes.CRUST)) {
                crustInventoryItems = inventoryItems.filter((item) => item.type === UKProductTypes.CRUST);
                const duplicatedOosPrices = _groupBy(oosPrices, (price) => price.inventoryItemId);
                const cloneOosPrices = _cloneDeep(oosPrices);
                for (const inventoryItemId of Object.keys(duplicatedOosPrices)) {
                    if (duplicatedOosPrices[inventoryItemId].length > 1) {
                        const duplicatedIdx = _findIndex(cloneOosPrices, { inventoryItemId: +inventoryItemId });
                        cloneOosPrices.splice(duplicatedIdx, 1);
                    }
                }
                const oosCrustItems = innerJoinPricesWithInventoryItems(cloneOosPrices, crustInventoryItems);
                groupedOosCrustItems = _groupBy(oosCrustItems, (item) => [item.inventoryItem.title]);
                oosCrustInventoryItemIds = getInventoryItemIdsByTitle(crustInventoryItems, Object.keys(groupedOosCrustItems));
            }

            const getInStockPrices = (inventoryItemIds) => {
                const prices = this.PRICE_ITEMS.filter((p) => {
                    return (
                        p.active === true &&
                        p.available === true &&
                        (p.storeId === store.id ||
                            (p.storeGroupId === store.storeGroupId && _isEmpty(p.store)) ||
                            (_isEmpty(p.storeGroup) && _isEmpty(p.store)))
                    );
                }).filter((p) => inventoryItemIds.includes(p.inventoryItemId));
                return prices;
            };
            const [instockPizzaPrices, instockCrustPrices] = [
                getInStockPrices(oosPizzaInventoryItemIds),
                getInStockPrices(oosCrustInventoryItemIds)
            ];
            const instockPizzaItems = innerJoinPricesWithInventoryItems(
                _uniqBy(instockPizzaPrices, (price) => price.inventoryItemId),
                pizzaInventoryItems
            );
            const instockCrustItems = innerJoinPricesWithInventoryItems(
                _uniqBy(instockCrustPrices, (price) => price.inventoryItemId),
                crustInventoryItems
            );

            const groupedInstockPizzaItems = _groupBy(instockPizzaItems, (item) => [item.inventoryItem.title]);
            const groupedInstockCrustItems = _groupBy(instockCrustItems, (item) => [item.inventoryItem.title]);

            // Get pizza oos items and crust oos items
            oosPizzaCrustItems.push(...getOosItems(groupedOosPizzaItems, groupedInstockPizzaItems));
            oosPizzaCrustItems.push(...getOosItems(groupedOosCrustItems, groupedInstockCrustItems));

            return [...oosSingleItems, ...oosPizzaCrustItems];
        } catch (error) {
            console.log(error)
            throw error;
        }
    }

    filterInventoryItems(productTypes, searchText = '') {
        if (_isEmpty(productTypes)) {
            return [];
        }

        const productTypeList = productTypes.split(Delimiters.COMMA);
        const identity = (item) => {
            const productTypeCondition = productTypeList.includes(item.type);
            const searchTextCondition = item.title.toLowerCase().includes(searchText.toLowerCase().trim());
            return productTypeCondition && searchTextCondition;
        };

        return this.getAllInventoryItems().filter(identity);
    }

    searchInventoryItems(productTypes, searchText = '') {
        //pizza,crust
        const specialTypes = [UKProductTypes.PIZZA, UKProductTypes.CRUST];
        //side,dip,dessert,drink
        const filterProductTypes = productTypes
            .split(Delimiters.COMMA)
            .filter((p) => !specialTypes.includes(p))
            .join(Delimiters.COMMA);

        //Get search items for side,dip,dessert,drink
        const filteredItems = this.filterInventoryItems(filterProductTypes, searchText).map(
            (item) => ({
                groupName: item.name,
                ...item
            })
        );

        if (productTypes.includes(UKProductTypes.PIZZA)) {
            const pizzaItemsByTitle = this.getPizzas(searchText);
            filteredItems.push(...pizzaItemsByTitle);
        }
        if (productTypes.includes(UKProductTypes.CRUST)) {
            const pizzaItemsByCrust = this.getPizzasByCrust(searchText);
            filteredItems.push(...pizzaItemsByCrust);
        }

        return _orderBy(filteredItems, ['type', 'groupName', 'id']);
    }

    getMasterCrusts(title) {
        return _uniqBy(this.filterInventoryItems(UKProductTypes.CRUST, title), 'name').sort(
            (c1, c2) => c2.name.length - c1.name.length
        );
    }

    getPizzas(title) {
        //Get all crust ids. To extract all pizza name only
        const crusts = this.getMasterCrusts(title);

        if (_isEmpty(crusts)) {
            return [];
        }
        //Build crust regexp (Ex: bacon-n-cheese-stuffed-crust|bm-classic|pan)
        const crustIdsRegExpPattern = `(${crusts.map(({ name }) => name).join(Delimiters.BAR)})`;

        //Extract pizza name from id
        const pizzas = this.filterInventoryItems(UKProductTypes.PIZZA, title); //P1-C1, P1-C2 => inventoryItem

        const filteredPizzas = pizzas
            .map((p) => {
                const [pizzaName] = p.name.split(new RegExp(`.{1}${crustIdsRegExpPattern}.{1}`));
                if (_isEmpty(pizzaName)) {
                    return;
                }
                return {
                    ...p,
                    groupName: pizzaName,
                    type: UKProductTypes.PIZZA
                };
            })
            .filter((p) => p);
        return filteredPizzas;
    }

    getPizzasByCrust(title) {
        //Get crust by title
        const crusts = this.getMasterCrusts(title); //bm-pan, limited-pan, pan
        if (_isEmpty(crusts)) {
            return [];
        }
        //Extract pizza name from id
        const filteredPizzas = this.filterInventoryItems(UKProductTypes.PIZZA)
            .map((p) => {
                const crust = crusts.find((c) => p.name.includes(c.name));
                if (_isEmpty(crust)) {
                    return;
                }
                return {
                    ...p,
                    groupName: crust.name, //limited-pan
                    title: crust.title, // Pan
                    type: UKProductTypes.CRUST
                };
            })
            .filter((p) => p);
        return filteredPizzas;
    }
}



// WEBPACK FOOTER //
// ./src/utils/oos/ExportOOS.js